/**
 * useUpdateCrmAssociation — set the targets of one association type on one
 * object, as a single optimistic mutation.
 *
 * The inline-editing counterpart to useUpdateCrmProperty: the caller hands
 * over the object (at mutate time — not a stale closure), the association
 * type, and the DESIRED list of target ids. The hook diffs that against
 * the object's current associations and submits the minimal set of
 * ahoy_association_added / ahoy_association_removed events in one batch.
 *
 * An inline cell editor works with the complete replacement list (the
 * picker's value), so the diff belongs here, next to the submit — not in
 * every caller.
 *
 * The desired list is complete *within its scope*: pass
 * `targetEntityTypeName` when the picker covers only one target type — a field
 * rendering one subset of a kind-based rule — so the diff cannot read the other
 * subsets as removals. The write is a delta either way; the scope only decides
 * which targets are eligible to appear in it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CrmObject } from '../../../types/crm';
import type { EntityTypeName } from '../core/types';
import { crmQueryKeys, fetchCrmObjectDetail } from '../query/queries';
import { diffTargetSubset } from '../record/utils/associationTargetSubsets';
import { getStoredObject, resolveEntityId } from '../store/crmObjectStore';
import { submitEventsOptimistic } from '../store/submitEventsOptimistic';

export interface UpdateCrmAssociationVariables {
  /** The object whose associations are being edited (mutate-time snapshot). */
  object: CrmObject;
  /** Association type whose target set is being replaced. */
  associationTypeName: string;
  /** The desired complete list of target entity ids, within the scope below. */
  targetIds: string[];
  /**
   * Scopes the replacement to targets of ONE entity type.
   *
   * A kind-based rule (`ahoy_contact_to_deal`, target kind `case`) resolves to
   * every case-kind type, and is rendered as one field per type — so that
   * field's picker yields a complete list for ITS type only. Without this the
   * diff below would read every other type's targets as removals: editing
   * "Deals" would unlink the record's tasks.
   *
   * Omit for a rule that names a single target type; the whole set is then in
   * scope, which is the original behaviour.
   */
  targetEntityTypeName?: string;
  /**
   * The target ids the calling field rendered for its subset.
   *
   * The store lookup below can miss an entry that was evicted between render
   * and save, which would drop a removal silently. A field knows exactly which
   * targets it displayed, so it states them and they skip the lookup. Ignored
   * unless `targetEntityTypeName` is set.
   */
  knownSubsetTargetIds?: string[];
  /**
   * When explicitly `false`, the mutation refuses to emit any
   * `ahoy_association_removed` event and rejects instead of submitting one.
   *
   * A caller sets this when the acting user holds the add grant but not the
   * remove grant, and its editor was offered on the assumption the write would
   * be add-only (e.g. an ownerless task's first assignee). The target set can
   * change under that caller between render and save — an owner appears — which
   * turns the write into a reassign whose removal the user cannot make.
   * Checking here, against the FRESH snapshot the diff is computed from, closes
   * that race: an inevitable server 403 becomes a clear client-side rejection
   * the inline editor surfaces. Omit (or `true`) to allow removals — the
   * default for every association editor.
   */
  canRemoveExistingTargets?: boolean;
}

/**
 * The delta when the whole target set is in scope — the rule names a single
 * target type, so the desired list is complete and anything missing from it is
 * a removal. (The scoped counterpart is `diffTargetSubset`.)
 */
function diffWholeSet(
  currentIds: readonly string[],
  targetIds: readonly string[]
): { added: string[]; removed: string[] } {
  const currentSet = new Set(currentIds);
  const desiredSet = new Set(targetIds);
  return {
    added: targetIds.filter(id => !currentSet.has(id)),
    removed: currentIds.filter(id => !desiredSet.has(id)),
  };
}

/**
 * React Query mutation replacing one association type's targets on an object.
 *
 * @param teamId - Organization ID
 * @param userId - ID of the authenticated user performing the action
 */
export function useUpdateCrmAssociation(
  teamId: number | undefined,
  userId: number | undefined
) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateCrmAssociationVariables>({
    mutationFn: async ({
      object,
      associationTypeName,
      targetIds,
      targetEntityTypeName,
      knownSubsetTargetIds,
      canRemoveExistingTargets,
    }) => {
      if (!teamId || userId == null) {
        throw new Error('Team ID and user are required');
      }

      const entityType = object.entity_type_name as EntityTypeName;

      // Fetch fresh state so the diff and the seq are computed against the
      // server's truth, not a possibly-stale list row. Associations are also
      // mutated by agents/automations, so the row snapshot ages quickly.
      // crmObjectDetailQuery's queryFn upserts into crmObjectStore and
      // returns a null sentinel — fetchCrmObjectDetail reads the object back
      // out of the store.
      let baseObject = object;
      try {
        baseObject = await fetchCrmObjectDetail(
          queryClient,
          teamId,
          entityType,
          object.entity_id
        );
      } catch {
        // Network hiccup (or a store-generation race) — fall back to the
        // best available snapshot.
        baseObject =
          getStoredObject(resolveEntityId(object.entity_id))?.data ?? object;
      }

      const currentIds = (baseObject.associations ?? [])
        .filter(a => a.association_type_name === associationTypeName)
        .map(a => a.target_entity_id);

      // Scoped to one target type when the caller is editing a single subset of
      // a kind-based rule; otherwise the whole set is in scope. Either way the
      // write is a delta — the events below are exactly what changed.
      //
      // An association row carries no target entity type (AssociationDetail is
      // {association_type_name, target_entity_id}), so the type is resolved from
      // the object store — with the caller's rendered ids taking precedence, so
      // an evicted store entry cannot silently drop a removal. An id that is
      // neither rendered here nor resolvable is out of scope and untouched.
      const { added, removed } = targetEntityTypeName
        ? diffTargetSubset({
            currentTargetIds: currentIds,
            targetEntityTypeName,
            nextTargetIds: targetIds,
            knownSubsetTargetIds,
            resolveTargetEntityType: id =>
              getStoredObject(resolveEntityId(id))?.data?.entity_type_name,
          })
        : diffWholeSet(currentIds, targetIds);

      if (added.length === 0 && removed.length === 0) {
        return null; // Nothing to do — treat as success.
      }

      // The fresh diff wants to remove a target, but the caller declared the
      // acting user can't. This is the race the render-time gate can't see: the
      // set changed after the editor opened. Reject before submitting so the
      // failure is a clear inline message, not a server 403.
      if (canRemoveExistingTargets === false && removed.length > 0) {
        throw new Error(
          "You don't have permission to remove the current value."
        );
      }

      // errorToast: false — the inline association cell editor rethrows to
      // show an inline error tooltip; a default toast would double-signal.
      return submitEventsOptimistic({
        orgId: teamId,
        userId,
        entityTypeName: entityType,
        primaryEntityId: object.entity_id,
        fallbackSeq: baseObject.last_event_seq,
        errorToast: false,
        build: b => {
          for (const id of added) {
            b.associationAdded(
              object.entity_id,
              object.entity_type_name,
              associationTypeName,
              id
            );
          }
          for (const id of removed) {
            b.associationRemoved(
              object.entity_id,
              object.entity_type_name,
              associationTypeName,
              id
            );
          }
        },
      });
    },

    onSettled: (_data, _error, variables) => {
      if (!teamId) {
        return;
      }
      const entityType = variables.object.entity_type_name as EntityTypeName;
      // Fire-and-forget: mutateAsync resolves when the submit resolves, so
      // the inline editor closes on commit rather than after every active
      // list query has refetched. Freshness is belt-and-braces here anyway —
      // the WebSocket echo lands the same change in the store.
      void queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objectDetail(
          teamId,
          entityType,
          variables.object.entity_id
        ),
        refetchType: 'none',
      });
      // The target objects' reverse associations changed too; their types are
      // unknown here, so refresh the object universe broadly.
      void queryClient.invalidateQueries({
        queryKey: ['crm', teamId, 'objects'],
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({
        queryKey: ['associations', teamId, variables.associationTypeName],
        refetchType: 'none',
      });
    },
  });
}
